import { Button, CardBackground, Typography } from "@hellocarbo/lasagne";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import useSegmentEvent from "@/hooks/useSegmentEvent";
import { TTrajectoryAction } from "@/types";

import ActionListItem from "./ActionListItem";

type AvailableActionsListProps = {
  availableActions: Array<TTrajectoryAction>;
};

function AvailableActionsList({ availableActions }: AvailableActionsListProps) {
  const { t, i18n } = useTranslation(["trajectory"]);
  const { sendSegmentEvent } = useSegmentEvent();

  return (
    <CardBackground>
      <div className="mb-sm flex items-center justify-between">
        <Typography as="h2" step="2" weight="bold">
          {t("trajectory:actionPlan.availableActions")}
        </Typography>
        <Button
          as="a"
          shouldOpenNewTab
          shape="link"
          intent="secondary"
          PrefixIcon={Plus}
          href="https://forms.gle/vJHJsDbrm7z7YBFf9"
          size="small"
          onClick={() => sendSegmentEvent("action-plan-action-suggestion")}
        >
          {t("trajectory:actionPlan.proposeAnAction")}
        </Button>
      </div>
      {availableActions.length ? (
        <ul className="gap-y-xs flex flex-col">
          {availableActions.map((availableAction) => {
            const name =
              i18n.language === "en"
                ? availableAction.name_en
                : availableAction.name_fr;
            const content =
              i18n.language === "en"
                ? availableAction.content_en
                : availableAction.content_fr;

            const {
              id: availableActionId,
              category,
              examples,
              status,
              reduction_factor,
              recommended,
              difficulty,
              reduction_tco2e,
              intensity_percentage,
              is_intensity_editable,
            } = availableAction;

            return (
              <ActionListItem
                key={availableActionId}
                name={name}
                content={content}
                category={category}
                examples={examples}
                status={status}
                reduction_factor={reduction_factor}
                recommended={recommended}
                difficulty={difficulty}
                reduction_tco2e={reduction_tco2e}
                intensity_percentage={intensity_percentage}
                isReadOnly={false}
                actionId={availableActionId}
                intensityIsEditable={is_intensity_editable}
              />
            );
          })}
        </ul>
      ) : (
        <div className="border-grayscale-xx-light p-sm rounded-lg border text-center">
          <Typography step="-1" className="text-grayscale">
            {t("trajectory:actionPlan.noAvailableActions")}
          </Typography>
        </div>
      )}
    </CardBackground>
  );
}

export default AvailableActionsList;
